import {useRegistrationOperation} from '@/dashboard/patients/use-registration-operation';
/** Frozen Patient arrival: open the shared registration form in the same
 * centered window. Resolve an unlinked identity before capture; selecting a
 * record does not check in. The existing start API owns the session. */
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';

import {
  startIntakeSession,
  type StartIntakeResult,
} from '@/dashboard/appointments/intake-start-api';
import { AcButton } from '@/dashboard/ui';
import ModalShell from '@/dashboard/ui/ModalShell';
import {
  IntakeDoorSelectionView,
} from '@/dashboard/appointments/IntakeDrawer';
import PatientIntakeMode from '@/dashboard/appointments/PatientIntakeMode';
import { DESK_INTAKE_VOICE } from '@/dashboard/appointments/patient-intake-mode-view';

// --- Copy (exported for the deterministic render tests) ---------------------

export const REGISTRATION_MODAL_EYEBROW = 'Registration';
export const REGISTRATION_MODAL_QUESTION =
  'Will you fill it yourself, or shall we help?';
export const REGISTRATION_SELF_FILL_COPY =
  'Self-fill - WhatsApp link coming when messaging is enabled';
export const REGISTRATION_WE_HELP_COPY = 'We will help - open the form';

export interface CompleteRegistrationModalProps {
  readonly appointmentId: number;
  readonly patientLabel: string;
  readonly onClose: () => void;
  /** Refetch the day read (fired after any server-state change). */
  readonly onChanged: () => void;
  /** WALK-3 (C4): open STRAIGHT to the in-modal form, skipping the
   *  one-question choice -- the RESUME path for a digital session already in
   *  progress (its identity door was cleared when it first started). */
  readonly startInForm?: boolean;
  /** Injectable start door (tests). */
  readonly api?: { start: typeof startIntakeSession };
}

type ModalView = 'choice' | 'person_selection' | 'form';

export default function CompleteRegistrationModal({
  appointmentId,
  onClose,
  onChanged,
  startInForm = false,
  api = { start: startIntakeSession },
}: CompleteRegistrationModalProps): JSX.Element {
  const [view, setView] = useState<ModalView>(startInForm ? 'form' : 'choice');
  const operation = useRegistrationOperation(String(appointmentId));
  const [busy, setBusy] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [doorResult, setDoorResult] = useState<StartIntakeResult | null>(null);

  // WALK-3 (C1): We-help ELABORATES this modal into the form IN PLACE -- no
  // navigation, no new tab. The SAME reusable form-core (PatientIntakeMode)
  // renders inside the expanded modal under the desk voice.
  const openForm = (): void => {
    setView('form');
  };

  /** WE-HELP: start the ONE session (identity door first), then open the
   *  reusable form. The future self-fill path starts the same session and
   *  delivers the same form by link -- one component, two deliveries. */
  const startWeHelp = async (expectedBearer?: string): Promise<void> => {
    setBusy(true);
    setServerError(null);
    await (async () => {const bearer = expectedBearer ?? await operation.begin();
      await operation.check(bearer);
      const result = await api.start(appointmentId, 'digital', undefined, bearer);
      await operation.check(bearer); return {result,bearer};})()
      .then(async ({result,bearer}) => {
        let res = result;
        if (res.outcome === 'linked') {
          onChanged();
          await operation.check(bearer);
          res = await api.start(appointmentId, 'digital', undefined, bearer);
          await operation.check(bearer);
        }
        if (res.outcome === 'selection_required') {
          setDoorResult(res);
          setView('person_selection');
          return;
        }
        openForm();
      })
      .catch((err: unknown) => {
        if (!operation.isCurrent()) return;
        setServerError(
          err instanceof Error ? err.message : 'Could not open the registration form.',
        );
      })
      .finally(() => {if (operation.isCurrent()) setBusy(false);});
  };

  const pickDoorPatient = (patientId: string): void => {
    setBusy(true);
    setServerError(null);
    void (async () => {const bearer = await operation.begin();
      const result = await api.start(appointmentId, 'digital', { action: 'link', patientId }, bearer);
      await operation.check(bearer); return {result,bearer};})()
      .then(async ({bearer}) => {
        onChanged();
        await startWeHelp(bearer);
      })
      .catch((err: unknown) => {
        if (!operation.isCurrent()) return;
        setServerError(
          err instanceof Error ? err.message : 'Could not link the patient.',
        );
      })
      .finally(() => {if (operation.isCurrent()) setBusy(false);});
  };

  const confirmCreateNew = (): void => {
    setBusy(true);
    setServerError(null);
    void (async () => {const bearer = await operation.begin();
      const result = await api.start(appointmentId, 'digital', { action: 'create_new' }, bearer);
      await operation.check(bearer); return result;})()
      .then((res) => {
        if (res.outcome === 'selection_required') {
          setDoorResult(res);
          setView('person_selection');
          return;
        }
        openForm();
      })
      .catch((err: unknown) => {
        if (!operation.isCurrent()) return;
        setServerError(
          err instanceof Error ? err.message : 'Could not open the registration form.',
        );
      })
      .finally(() => {if (operation.isCurrent()) setBusy(false);});
  };

  useEffect(() => {
    if (!startInForm) startWeHelp();
  }, [appointmentId]);

  const body = (): JSX.Element => {
    switch (view) {
      case 'person_selection':
        return (
          <IntakeDoorSelectionView
            door={
              doorResult ?? {
                candidates: [],
                bookedAppointments: [],
                reconciliationPending: false,
                warnings: [],
              }
            }
            busy={busy}
            onPick={pickDoorPatient}
            onCreateNew={confirmCreateNew}
          />
        );
      case 'form':
        // WALK-3 (C1/C2/C3): the SAME reusable form-core, hosted IN this modal
        // under the DESK voice. On completion the day board refetches (the row
        // derives Ready for doctor) and the modal closes back to it.
        return (
          <PatientIntakeMode
            appointmentId={appointmentId}
            voice={DESK_INTAKE_VOICE}
            onBack={onClose}
            onBusyChange={setFormBusy}
            onComplete={() => {
              onChanged();
              onClose();
            }}
          />
        );
      default:
        return serverError ? <AcButton onClick={() => void startWeHelp()} disabled={busy}>Retry</AcButton> : <p role="status">Opening registration…</p>;
    }
  };

  return (
    <ModalShell trapFocus
      title="Complete registration"
      description="Finish the missing details, then check in."
      titleIcon={<Plus size={20}/>}
      onClose={() => { if (!busy && !formBusy) onClose(); }}
      testId="complete-registration-modal"
      closeDisabled={busy || formBusy}
      /* WALK-3 (C1): the modal expands to the wide working size when it hosts
         the form; the one-question choice + identity door stay compact. */
      size="lg"
      /* Close = X only (W-7): scrim and Escape never dismiss -- the desk
         closes deliberately, or proceeds. */
      closeOnScrim={false}
      closeOnEscape={false}
    >
      {serverError ? (
        <p className="intake-drawer__server-error" role="alert">
          {serverError}
        </p>
      ) : null}
      <div aria-busy={busy || formBusy}>{body()}</div>
    </ModalShell>
  );
}
